import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatCurrency, formatDate } from "../../core/common/api";
import MetricCard from "../../shared/MetricCard";
import PaginationBar, { slicePage } from "../../shared/PaginationBar";
import ToggleSwitch from "../../shared/ToggleSwitch";
import ResetButton from "../../shared/ResetButton";
import useTableSearch from "../../shared/useTableSearch";

type CouponForm = {
  code: string;
  name: string;
  description: string;
  discountType: "percent" | "fixed";
  discountValue: string;
  maxDiscountAmount: string;
  minOrderAmount: string;
  usageLimit: string;
  perUser: string;
  validFrom: string;
  expiresAt: string;
  isActive: boolean;
};

const toDateTimeLocal = (value?: string | Date | null) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const createEmptyForm = (): CouponForm => ({
  code: "",
  name: "",
  description: "",
  discountType: "fixed",
  discountValue: "",
  maxDiscountAmount: "",
  minOrderAmount: "",
  usageLimit: "",
  perUser: "",
  validFrom: toDateTimeLocal(new Date()),
  expiresAt: "",
  isActive: true,
});

const normalizeNumber = (value: string) => Number(value || 0);
const isCouponExpired = (coupon: any) =>
  Boolean(
    coupon.expiresAt && new Date(coupon.expiresAt).getTime() <= Date.now(),
  );

const Coupons = () => {
  const [searchParams] = useSearchParams();
  const [coupons, setCoupons] = useState<any[]>([]);
  const [form, setForm] = useState<CouponForm>(createEmptyForm);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const searchTerm = useTableSearch(search);
  const [typeFilter, setTypeFilter] = useState("all");
  const [isActiveFilter, setIsActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const loadCoupons = async () => {
    setLoading(true);

    try {
      const result = await apiRequest("/coupons", "GET");
      setCoupons(result.coupons || []);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load coupons", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons();
  }, []);

  const filteredCoupons = useMemo(() => {
    const query = searchTerm.toLowerCase();

    return coupons.filter((coupon) => {
      const searchableText = `
        ${coupon.code || ""}
        ${coupon.name || ""}
        ${coupon.description || ""}
      `.toLowerCase();

      const searchMatch = !query || searchableText.includes(query);
      const typeMatch =
        typeFilter === "all" || coupon.discountType === typeFilter;
      const isExpired = isCouponExpired(coupon);
      const isActiveMatch =
        isActiveFilter === "all" ||
        (isActiveFilter === "true" && coupon.isActive && !isExpired) ||
        (isActiveFilter === "false" && !coupon.isActive) ||
        (isActiveFilter === "expired" && isExpired);

      return searchMatch && typeMatch && isActiveMatch;
    });
  }, [coupons, searchTerm, typeFilter, isActiveFilter]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, typeFilter, isActiveFilter]);

  const paged = slicePage(filteredCoupons, page, limit);

  const metrics = [
    {
      label: "Total Coupons",
      value: coupons.length,
      icon: "🎟️",
      tone: "orange",
    },
    {
      label: "Active Coupons",
      value: coupons.filter((item) => item.isActive && !isCouponExpired(item)).length,
      icon: "✅",
      tone: "blue",
    },
    {
      label: "Inactive Coupons",
      value: coupons.filter((item) => item.isActive === false).length,
      icon: "⏳",
      tone: "purple",
    },
    {
      label: "Expired Coupons",
      value: coupons.filter(isCouponExpired).length,
      icon: "⌛",
      tone: "pink",
    },
  ];

  const handleChange = (event: any) => {
    const { name, value, type, checked } = event.target;

    setForm((oldForm) => ({
      ...oldForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(createEmptyForm());
    setEditingId("");
  };

  const validateForm = () => {
    if (!form.code.trim()) return "Coupon code is required.";
    if (!form.name.trim()) return "Coupon name is required.";
    if (!form.discountValue) return "Discount value is required.";

    const discountValue = normalizeNumber(form.discountValue);

    if (discountValue <= 0) return "Discount value must be greater than 0.";
    if (form.discountType === "percent" && discountValue > 100) {
      return "Percent discount cannot exceed 100.";
    }
    if (normalizeNumber(form.maxDiscountAmount) < 0) {
      return "Max discount cannot be negative.";
    }
    if (normalizeNumber(form.minOrderAmount) < 0) {
      return "Minimum order amount cannot be negative.";
    }
    if (normalizeNumber(form.usageLimit) < 0) {
      return "Usage limit cannot be negative.";
    }
    if (form.perUser && normalizeNumber(form.perUser) < 1) {
      return "Per user limit should be at least 1.";
    }

    const validFrom = new Date(form.validFrom);
    if (!form.validFrom || Number.isNaN(validFrom.getTime())) {
      return "Valid-from date is required.";
    }

    if (form.expiresAt) {
      const expiresAt = new Date(form.expiresAt);
      if (Number.isNaN(expiresAt.getTime())) {
        return "Expiry date must be valid.";
      }
      if (expiresAt <= validFrom) {
        return "Expiry date must be after the valid-from date.";
      }
    }

    return "";
  };

  const buildPayload = () => ({
    code: form.code.trim().toUpperCase(),
    name: form.name.trim(),
    description: form.description.trim(),
    discountType: form.discountType,
    discountValue: normalizeNumber(form.discountValue),
    maxDiscountAmount: normalizeNumber(form.maxDiscountAmount),
    minOrderAmount: normalizeNumber(form.minOrderAmount),
    usageLimit: normalizeNumber(form.usageLimit),
    perUser: form.perUser ? normalizeNumber(form.perUser) : 1,
    validFrom: new Date(form.validFrom).toISOString(),
    expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    isActive: form.isActive,
  });

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      Swal.fire("Validation Error", validationError, "error");
      return;
    }

    setSaving(true);

    try {
      const endpoint = editingId ? `/coupons/${editingId}` : "/coupons";
      const method = editingId ? "PUT" : "POST";
      const result = await apiRequest(endpoint, method, buildPayload());

      if (!result.success) {
        throw new Error(result.message || "Coupon save failed");
      }

      await Swal.fire({
        title: editingId ? "Coupon Updated" : "Coupon Added",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      resetForm();
      loadCoupons();
    } catch (error: any) {
      Swal.fire("Save Failed", error.message || "Coupon save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const editCoupon = (coupon: any) => {
    setForm({
      code: coupon.code || "",
      name: coupon.name || "",
      description: coupon.description || "",
      discountType: coupon.discountType || "percent",
      discountValue: String(coupon.discountValue ?? ""),
      maxDiscountAmount: String(coupon.maxDiscountAmount ?? 0),
      minOrderAmount: String(coupon.minOrderAmount ?? 0),
      usageLimit: String(coupon.usageLimit ?? 0),
      perUser: String(coupon.perUser ?? 1),
      validFrom: toDateTimeLocal(coupon.validFrom || coupon.createdAt),
      expiresAt: toDateTimeLocal(coupon.expiresAt),
      isActive: coupon.isActive !== false,
    });
    setEditingId(coupon._id);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const deleteCoupon = async (id: string) => {
    const confirmation = await Swal.fire({
      title: "Delete coupon?",
      text: "This coupon will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const result = await apiRequest(`/coupons/${id}`, "DELETE");

      if (!result.success) {
        throw new Error(result.message || "Coupon delete failed");
      }

      if (editingId === id) {
        resetForm();
      }

      await loadCoupons();
      Swal.fire({
        title: "Coupon Deleted",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire(
        "Delete Failed",
        error.message || "Coupon delete failed",
        "error",
      );
    }
  };

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("all");
    setIsActiveFilter("all");
  };

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MANAGEMENT</div>

          <h1 className="page-title">
            All <em>Coupons</em>
          </h1>

          <div className="page-sub">
            Manage coupon codes and discount rules.
          </div>
        </div>

        <div className="page-hdr-actions">
          <button
            type="button"
            className="btn-sm btn-primary"
            onClick={() => {
              resetForm();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add Coupon
          </button>
        </div>
      </div>

      <section className="metrics-grid">
        {metrics.map((item) => (
          <MetricCard key={item.label} metric={item} />
        ))}
      </section>

      <form className="card banner-page" onSubmit={handleSubmit} noValidate>
        <div className="card-header">
          <div>
            <h2>{editingId ? "Edit Coupon" : "Add Coupon"}</h2>
            <p>Fields marked with * are required.</p>
          </div>

          {editingId && <strong>Editing</strong>}
        </div>

        <div className="card-body">
          <div className="three-col">
            <div className="banner-field">
              <label>Coupon Code *</label>
              <input
                name="code"
                value={form.code}
                onChange={(event) =>
                  setForm({ ...form, code: event.target.value.toUpperCase() })
                }
                maxLength={40}
                placeholder="SUMMER20"
              />
            </div>
            <div className="banner-field">
              <label>Coupon Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Summer Sale Coupon"
              />
            </div>
            <div className="banner-field">
              <label>Total Usage Limit</label>
              <input
                type="number"
                min="0"
                name="usageLimit"
                value={form.usageLimit}
                onChange={handleChange}
                onFocus={(event) => event.currentTarget.select()}
                placeholder="0 means unlimited"
              />
            </div>
            <div className="banner-field">
              <label>Per User Limit</label>
              <input
                type="number"
                min="1"
                name="perUser"
                value={form.perUser}
                onChange={handleChange}
                onFocus={(event) => event.currentTarget.select()}
                placeholder="1"
              />
            </div>

            <div className="banner-field">
              <label>Discount Type *</label>
              <select
                name="discountType"
                value={form.discountType}
                onChange={handleChange}
              >
                <option value="percent">Percent</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            <div className="banner-field">
              <label>Discount Value *</label>
              <input
                type="number"
                min="0"
                name="discountValue"
                value={form.discountValue}
                onChange={handleChange}
                onFocus={(event) => event.currentTarget.select()}
                placeholder={form.discountType === "percent" ? "20" : "500"}
              />
            </div>

            <div className="banner-field">
              <label>Max Discount Amount</label>
              <input
                type="number"
                min="0"
                name="maxDiscountAmount"
                value={form.maxDiscountAmount}
                onChange={handleChange}
                onFocus={(event) => event.currentTarget.select()}
                placeholder="0 means no max cap"
              />
            </div>

            <div className="banner-field">
              <label>Minimum Order Amount</label>
              <input
                type="number"
                min="0"
                name="minOrderAmount"
                value={form.minOrderAmount}
                onChange={handleChange}
                onFocus={(event) => event.currentTarget.select()}
                placeholder="0"
              />
            </div>

            <div className="banner-field">
              <label>Valid From *</label>
              <input
                type="datetime-local"
                name="validFrom"
                value={form.validFrom}
                onChange={handleChange}
              />
            </div>

            <div className="banner-field">
              <label>Expiry Date</label>
              <input
                type="datetime-local"
                name="expiresAt"
                value={form.expiresAt}
                min={form.validFrom || undefined}
                onChange={handleChange}
              />
            </div>

            <div className="banner-field">
              <label>Status</label>
              <ToggleSwitch
                checked={form.isActive}
                onChange={(isActive) => setForm((current) => ({ ...current, isActive }))}
                checkedLabel="Active"
                uncheckedLabel="Inactive"
              />
            </div>
          </div>

          <div className="banner-field">
            <label>Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              maxLength={1000}
              rows={4}
              placeholder="Write coupon description..."
            />
            <small>{form.description.length}/1000</small>
          </div>
        </div>

        <div className="card-footer">
          <button
            type="button"
            className="btns btn-light"
            onClick={resetForm}
            disabled={saving}
          >
            ✖ Clear
          </button>

          <button type="submit" className="btns btn-primary" disabled={saving}>
            💾 {saving ? "Saving..." : editingId ? "Update" : "Save"}
          </button>
        </div>
      </form>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={search}
            placeholder="Search code, name, description"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
        >
          <option value="all">All Types</option>
          <option value="percent">Percent</option>
          <option value="fixed">Fixed Amount</option>
        </select>

        <select
          className="filter-select"
          value={isActiveFilter}
          onChange={(event) => setIsActiveFilter(event.target.value)}
        >
          <option value="all">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
          <option value="expired">Expired</option>
        </select>

        <ResetButton onClick={resetFilters} />
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Coupons List</h2>
          <p>Manage Coupons</p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Coupon</th>
                <th>Discount</th>
                <th>Min Order</th>
                <th>Usage</th>
                <th>Per User</th>
                <th>Validity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    Loading coupons...
                  </td>
                </tr>
              ) : filteredCoupons.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    No coupons found.
                  </td>
                </tr>
              ) : (
                paged.rows.map((item) => {
                  const usageCap = Number(item.usageLimit || 0);
                  const isExpired = isCouponExpired(item);
                  const status = isExpired
                    ? "Expired"
                    : item.isActive
                      ? "Active"
                      : "Inactive";

                  return (
                    <tr key={item._id}>
                      <td>
                        <div className="img-name-cell coupon-code-cell">
                          <span className="coupon-code-badge">{item.code}</span>

                          <div>
                            <strong>{item.name}</strong>
                            <small>{item.description || "No description"}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        {item.discountType === "percent"
                          ? `${item.discountValue}%`
                          : formatCurrency(item.discountValue)}
                        {Number(item.maxDiscountAmount || 0) > 0 && (
                          <small className="table-muted">
                            Max {formatCurrency(item.maxDiscountAmount)}
                          </small>
                        )}
                      </td>

                      <td>{formatCurrency(item.minOrderAmount)}</td>
                      <td>
                        {item.usedCount || 0}
                        {usageCap > 0 ? ` / ${usageCap}` : " / Unlimited"}
                      </td>
                      <td>{item.perUser || 0}</td>
                      <td>
                        <strong>
                          {item.expiresAt ? formatDate(item.expiresAt) : "No expiry"}
                        </strong>
                        <small className="table-muted">
                          Starts {formatDate(item.validFrom || item.createdAt)}
                        </small>
                      </td>

                      <td>
                        <span
                          className={`status-badge ${
                            status === "Active" ? "success" : "danger"
                          }`}
                        >
                          {status}
                        </span>
                      </td>

                      <td>
                        <div className="actions-btns">
                          <button
                            type="button"
                            title="Edit"
                            className="action-button edit"
                            onClick={() => editCoupon(item)}
                          >
                            ✏️
                          </button>

                          <button
                            type="button"
                            title="Delete"
                            className="action-button delete"
                            onClick={() => deleteCoupon(item._id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={paged.page}
          limit={limit}
          total={paged.total}
          totalPages={paged.totalPages}
          itemLabel="coupons"
          onPageChange={setPage}
          onLimitChange={(nextLimit) => {
            setPage(1);
            setLimit(nextLimit);
          }}
        />
      </section>
    </>
  );
};

export default Coupons;
